import React, { useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Tooltip,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Divider,
  Heading,
} from '@chakra-ui/react';
import { formatNameLastCommaFirst, compareNamesByFamilyName } from '../utils/clientNameDisplay';
import { formatMakeupBadgeDate } from '../utils/attendanceDisplay';

const NAME_COLUMN_WIDTH = 200;
const DATE_COLUMN_WIDTH = 88;
const TOTAL_COLUMN_WIDTH = 64;

const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatTime = (time) => {
  if (!time) return '';
  const hour = parseInt(time.split(':')[0], 10);
  const h = hour % 12 || 12;
  const ampm = hour < 12 ? 'AM' : 'PM';
  return `${h}:00 ${ampm}`;
};

const isPendingMakeup = (session) =>
  session.attendance?.makeup_scheduled === false && !session.attendance?.paid_noshow;

const isPendingNoShowFee = (session) => session.attendance?.paid_noshow === false;

const getSessionTypeLabel = (session) => {
  if (isPendingMakeup(session)) return 'Makeup pending';
  if (isPendingNoShowFee(session)) return 'No-show fee pending';
  return 'Pending';
};

const buildSessionTooltip = (session) => {
  const therapist = session.therapist?.name || 'Unknown therapist';
  const type = getSessionTypeLabel(session);
  const time = session.time ? formatTime(session.time) : '';
  return `${type} · ${therapist}${time ? ` · ${time}` : ''}`;
};

export const buildPendingMakeupByClient = (sessions) => {
  const byClient = {};

  sessions.forEach((session) => {
    const clientId = session.client?.id;
    if (!clientId) return;

    if (!byClient[clientId]) {
      byClient[clientId] = {
        client: session.client,
        sessions: [],
      };
    }

    byClient[clientId].sessions.push(session);
  });

  const rows = Object.values(byClient)
    .map((row) => ({
      ...row,
      sessions: [...row.sessions].sort((a, b) => {
        const dateCompare = new Date(a.attendance_date) - new Date(b.attendance_date);
        if (dateCompare !== 0) return dateCompare;
        return parseInt(a.time || '0', 10) - parseInt(b.time || '0', 10);
      }),
      total: row.sessions.length,
    }))
    .sort(
      (a, b) => b.total - a.total || compareNamesByFamilyName(a.client, b.client)
    );

  const maxColumns = rows.reduce((max, row) => Math.max(max, row.total), 0);

  return { rows, maxColumns };
};

const PendingMakeupByClientView = ({ pendingSessions = [], scheduledSessions = [] }) => {
  const { rows, maxColumns } = useMemo(
    () => buildPendingMakeupByClient(pendingSessions),
    [pendingSessions]
  );

  const sortedScheduled = useMemo(
    () =>
      [...scheduledSessions].sort((a, b) => {
        const dateCompare = new Date(a.scheduledMakeupDate) - new Date(b.scheduledMakeupDate);
        if (dateCompare !== 0) return dateCompare;
        return (a.client?.name || '').localeCompare(b.client?.name || '');
      }),
    [scheduledSessions]
  );

  if (!rows.length && !sortedScheduled.length) {
    return (
      <Center py={8}>
        <Text color="gray.500">No pending makeup sessions or scheduled future makeups found</Text>
      </Center>
    );
  }

  const tableMinWidth =
    NAME_COLUMN_WIDTH + maxColumns * DATE_COLUMN_WIDTH + TOTAL_COLUMN_WIDTH;

  return (
    <VStack align="stretch" spacing={6}>
      {rows.length > 0 && (
        <Box borderWidth={1} borderRadius="md" overflow="hidden" bg="white">
          <Box overflowX="auto" overflowY="auto" maxH="70vh">
            <Box minW={`${tableMinWidth}px`}>
              <HStack
                spacing={0}
                borderBottomWidth={1}
                bg="gray.50"
                position="sticky"
                top={0}
                zIndex={2}
              >
                <Box
                  w={`${NAME_COLUMN_WIDTH}px`}
                  flexShrink={0}
                  px={4}
                  py={3}
                  borderRightWidth={1}
                  bg="gray.50"
                  position="sticky"
                  left={0}
                  zIndex={3}
                >
                  <Text fontSize="sm" fontWeight="bold" color="gray.600">
                    Client
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    Pending count, then last name
                  </Text>
                </Box>

                {maxColumns > 0 && (
                  <Box
                    flex={1}
                    minW={`${maxColumns * DATE_COLUMN_WIDTH}px`}
                    px={3}
                    py={3}
                    textAlign="center"
                    borderRightWidth={1}
                    borderColor="gray.100"
                  >
                    <Text fontSize="sm" fontWeight="bold" color="gray.600">
                      Pending dates
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      Oldest → newest
                    </Text>
                  </Box>
                )}

                <Box
                  w={`${TOTAL_COLUMN_WIDTH}px`}
                  flexShrink={0}
                  px={2}
                  py={3}
                  textAlign="center"
                  borderLeftWidth={1}
                  bg="purple.50"
                  position="sticky"
                  right={0}
                  zIndex={3}
                >
                  <Text fontSize="sm" fontWeight="bold" color="purple.700">
                    Total
                  </Text>
                </Box>
              </HStack>

              <VStack spacing={0} align="stretch">
                {rows.map((row) => (
                  <HStack
                    key={row.client.id}
                    spacing={0}
                    minH="44px"
                    borderBottomWidth={1}
                    borderColor="gray.100"
                    _hover={{ bg: 'gray.50' }}
                  >
                    <Box
                      w={`${NAME_COLUMN_WIDTH}px`}
                      flexShrink={0}
                      px={4}
                      py={2}
                      borderRightWidth={1}
                      bg="white"
                      position="sticky"
                      left={0}
                      zIndex={1}
                      _hover={{ bg: 'gray.50' }}
                    >
                      <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                        {formatNameLastCommaFirst(row.client.name)}
                      </Text>
                      {row.client.nickname && (
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>
                          {row.client.nickname}
                        </Text>
                      )}
                    </Box>

                    {Array.from({ length: maxColumns }, (_, index) => {
                      const session = row.sessions[index];

                      return (
                        <Box
                          key={`${row.client.id}-${index}`}
                          w={`${DATE_COLUMN_WIDTH}px`}
                          flexShrink={0}
                          px={1}
                          py={2}
                          textAlign="center"
                          borderRightWidth={1}
                          borderColor="gray.100"
                        >
                          {session ? (
                            <Tooltip
                              label={buildSessionTooltip(session)}
                              hasArrow
                              placement="top"
                            >
                              <Badge
                                colorScheme={isPendingNoShowFee(session) ? 'orange' : 'purple'}
                                fontSize="xs"
                                cursor="default"
                                whiteSpace="nowrap"
                              >
                                {formatShortDate(session.attendance_date)}
                              </Badge>
                            </Tooltip>
                          ) : (
                            <Text fontSize="xs" color="gray.300">
                              —
                            </Text>
                          )}
                        </Box>
                      );
                    })}

                    <Box
                      w={`${TOTAL_COLUMN_WIDTH}px`}
                      flexShrink={0}
                      px={2}
                      py={2}
                      textAlign="center"
                      borderLeftWidth={1}
                      bg="purple.50"
                      position="sticky"
                      right={0}
                      zIndex={1}
                    >
                      <Badge colorScheme="purple" fontSize="sm">
                        {row.total}
                      </Badge>
                    </Box>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </Box>

          <HStack px={4} py={2} bg="gray.50" borderTopWidth={1} spacing={4} flexWrap="wrap">
            <Text fontSize="xs" color="gray.600">
              Dates read left to right, oldest pending first
            </Text>
            <HStack spacing={1}>
              <Badge colorScheme="purple" fontSize="xs">
                date
              </Badge>
              <Text fontSize="xs" color="gray.600">
                Pending makeup
              </Text>
            </HStack>
            <HStack spacing={1}>
              <Badge colorScheme="orange" fontSize="xs">
                date
              </Badge>
              <Text fontSize="xs" color="gray.600">
                Pending no-show fee
              </Text>
            </HStack>
          </HStack>
        </Box>
      )}

      {sortedScheduled.length > 0 && (
        <Box>
          <Divider mb={4} />
          <Heading size="sm" mb={3} color="blue.700">
            Scheduled Future Makeup Sessions
            <Badge ml={2} colorScheme="blue">
              {sortedScheduled.length}
            </Badge>
          </Heading>
          <Box borderWidth={1} borderRadius="md" overflow="hidden" bg="white">
            <Box overflowX="auto" overflowY="auto" maxH="50vh">
              <Table size="sm" variant="simple">
                <Thead bg="blue.50">
                  <Tr>
                    <Th>Client</Th>
                    <Th>Original Date</Th>
                    <Th>Scheduled Date</Th>
                    <Th>Time</Th>
                    <Th>Therapist</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedScheduled.map((session) => (
                    <Tr key={session.id || session.attendance?.id}>
                      <Td>
                        <Text fontSize="sm" fontWeight="medium">
                          {formatNameLastCommaFirst(session.client?.name)}
                        </Text>
                        {session.client?.nickname && (
                          <Text fontSize="xs" color="gray.500">
                            {session.client.nickname}
                          </Text>
                        )}
                      </Td>
                      <Td whiteSpace="nowrap">
                        {session.originalMakeupDate || session.attendance_date
                          ? formatMakeupBadgeDate(session.originalMakeupDate || session.attendance_date)
                          : '—'}
                      </Td>
                      <Td whiteSpace="nowrap">
                        <Badge colorScheme="blue">
                          {formatMakeupBadgeDate(session.scheduledMakeupDate)}
                        </Badge>
                      </Td>
                      <Td whiteSpace="nowrap">
                        {formatTime(session.scheduledMakeupTime)}
                      </Td>
                      <Td>
                        <Text fontSize="sm">{session.therapist?.name}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {session.therapist?.specialization}
                        </Text>
                      </Td>
                      <Td>
                        <Badge colorScheme="blue" variant="outline">
                          Scheduled
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </Box>
      )}
    </VStack>
  );
};

export default PendingMakeupByClientView;
